import {
  BBox,
  entityBBox,
  entityHandles,
  makeConstraint,
  nearestEdge,
  nearestHandle,
  solveConstraints,
  samePoint,
  constraintEntityIds,
  type Constraint,
  type ConstraintKind,
  type EdgeRef,
  type Entity,
  type PointRef,
  type Vec2,
} from '@lakaran/geometry';
import type { CadDocument } from './document';
import { Transaction } from './history';

export interface SolveSummary {
  changed: number;
  residual: number;
  satisfied: number;
  total: number;
}

export interface SolveOutcome {
  tx: Transaction;
  summary: SolveSummary;
}

/**
 * Runs the constraint solver over the document and returns the entity updates as a
 * transaction, so the caller can commit them to the history like any other edit.
 * `enabled` filters which kinds are enforced — the toolbar chips toggle them.
 */
export function solveDocument(
  doc: CadDocument,
  enabled?: ReadonlySet<ConstraintKind>,
): SolveOutcome {
  const constraints = [...doc.constraints.values()].filter(
    (constraint) => !enabled || enabled.has(constraint.kind),
  );
  const tx = new Transaction();
  if (constraints.length === 0) {
    return { tx, summary: { changed: 0, residual: 0, satisfied: 0, total: 0 } };
  }

  const ids = new Set<string>();
  for (const constraint of constraints) {
    for (const id of constraintEntityIds(constraint)) ids.add(id);
  }

  const entities: Entity[] = [];
  for (const id of ids) {
    const entity = doc.get(id);
    if (entity) entities.push(entity);
  }

  const result = solveConstraints(entities, constraints);

  for (const after of result.entities) {
    const before = doc.get(after.id);
    if (!before) continue;
    tx.update(before, after);
  }

  return {
    tx,
    summary: {
      changed: result.entities.length,
      residual: result.residual,
      satisfied: result.satisfied,
      total: result.total,
    },
  };
}

// ------------------------------------------------------------------ snapping

export type PickTarget =
  /** Snapped onto a vertex or centre: binds the geometry by handle. */
  | { kind: 'handle'; ref: PointRef; point: Vec2; snap?: string }
  /** Snapped onto an edge: binds the point to that segment. */
  | { kind: 'edge'; edge: EdgeRef; point: Vec2; snap?: string }
  /** Snapped into empty space (grid, intersection of nothing): pins the coordinate. */
  | { kind: 'free'; point: Vec2; snap?: string };

/**
 * Resolves what the user actually pointed at. This is where a *snap* becomes a
 * *constraint*: vertex/centre → handle, on-edge → edge, anything else → fixed point.
 * `prefer` decides which is tested first, so an edge pick is not stolen by a nearby
 * endpoint.
 */
export function pickConstraintTarget(
  doc: CadDocument,
  point: Vec2,
  tolerance: number,
  snap?: string,
  prefer: 'point' | 'edge' = 'point',
): PickTarget {
  const nearby = doc.query(BBox.around(point, tolerance));
  const handleHit = nearestHandle(nearby, point, tolerance);
  const edgeHit = nearestEdge(nearby, point, tolerance);

  if (prefer === 'edge') {
    if (edgeHit) return { kind: 'edge', edge: edgeHit.edge, point: edgeHit.point, snap };
    if (handleHit) return { kind: 'handle', ref: handleHit.ref, point: handleHit.point, snap };
  } else {
    if (handleHit) return { kind: 'handle', ref: handleHit.ref, point: handleHit.point, snap };
    if (edgeHit) return { kind: 'edge', edge: edgeHit.edge, point: edgeHit.point, snap };
  }

  return { kind: 'free', point: point.clone(), snap };
}

const ATTACH_TOLERANCE = 1e-6;

/**
 * Records vertex snaps as coincident constraints: every handle of `entities` that sits
 * exactly on another entity's handle gets pinned to it. Called when new geometry is
 * committed, so drawing with endpoint/centre snaps produces a solved parametric model.
 */
export function attachNearbyHandles(
  doc: CadDocument,
  entities: readonly Entity[],
  tx: Transaction,
  tolerance = ATTACH_TOLERANCE,
): number {
  let created = 0;

  for (const entity of entities) {
    const nearby = doc.query(entityBBox(entity).expand(tolerance));
    for (const handle of entityHandles(entity)) {
      for (const other of nearby) {
        if (other.id === entity.id) continue;
        for (const otherHandle of entityHandles(other)) {
          if (handle.point.distanceTo(otherHandle.point) > tolerance) continue;
          const point: PointRef = { entityId: entity.id, handle: handle.name };
          const target: PointRef = { entityId: other.id, handle: otherHandle.name };
          if (hasCoincident(doc, point, target)) continue;
          const constraint = makeConstraint({
            kind: 'coincident',
            point,
            target,
            source: 'snap' as const,
            snap: 'endpoint',
          });
          doc.addConstraint(constraint);
          tx.addConstraint(constraint);
          created += 1;
        }
      }
    }
  }

  return created;
}

function hasCoincident(doc: CadDocument, point: PointRef, target: PointRef): boolean {
  for (const constraint of doc.constraints.values()) {
    if (constraint.kind !== 'coincident') continue;
    if (samePoint(constraint.point, point) && samePoint(constraint.target, target)) return true;
    if (samePoint(constraint.point, target) && samePoint(constraint.target, point)) return true;
  }
  return false;
}

/** Short human summary used by the status bar and `CONSTRAINTS` command. */
export function describeConstraintSet(doc: CadDocument): string {
  const counts = new Map<string, number>();
  for (const constraint of doc.constraints.values()) {
    counts.set(constraint.kind, (counts.get(constraint.kind) ?? 0) + 1);
  }
  if (counts.size === 0) return 'no constraints';
  return [...counts].map(([kind, count]) => `${count}× ${kind}`).join(', ');
}

export type { Constraint };
